import React from "react";
import { convertTime } from "../../../../../../helpers/helpers";
import { useOrderVisibility } from "../../../../../../helpers/useOrderVisibility";
import { useTranslation } from "react-i18next";

// Mirrors the de-duplication the previous view did: address_1 often already
// contains the city/postcode, so only append parts it doesn't mention.
const composeAddress = (address) => {
  const {
    address_1 = "",
    postcode = "",
    city = "",
    state_name = "",
    country_name = "",
  } = address || {};

  const lower = address_1.toLowerCase();
  const parts = address_1 ? [address_1] : [];

  [postcode, city, state_name, country_name].forEach((part) => {
    if (part && !lower.includes(String(part).toLowerCase())) parts.push(part);
  });

  return parts.join(", ");
};

const Card = ({ label, children, className = "" }) => (
  <div
    className={`min-w-0 rounded-lg border border-ui-border bg-ui-surface px-3 py-2.5 ${className}`}
  >
    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
      {label}
    </div>
    {children}
  </div>
);

// Values wrap on phones (nothing cut off); from sm up they truncate as before.
const Field = ({ label, value, accent = false }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-ui-text-subtle">
      {label}
    </div>
    <div
      className={`break-words text-[12px] font-semibold sm:truncate ${
        accent ? "text-state-success-text" : "text-ui-text"
      }`}
    >
      {value || "—"}
    </div>
  </div>
);

const StripItem = ({ label, value, link = false }) => (
  <span className="flex min-w-0 items-baseline gap-1.5">
    <span className="shrink-0 text-[12px] text-ui-text-subtle sm:text-[11px]">
      {label}
    </span>
    <span
      className={`min-w-0 break-words text-[12px] font-semibold sm:truncate ${
        link ? "text-brand-text" : "text-ui-text"
      }`}
    >
      {value || "—"}
    </span>
  </span>
);

// `unit` on the shipment is the measurement system, not a weight suffix.
const weightUnit = (unit) => (unit === "Metric" ? "kg" : "lb");

export default function OrderSummary({ data, AdditionalCostTotal }) {
  const { t } = useTranslation("warehouse");
  // Amounts need "View price"; a cash order's amount to collect still
  // shows, as the server sends it to every role.
  const { showPrices } = useOrderVisibility();
  const showPending = showPrices || data?.amount_pending != null;
  const pickup = data?.order_pickup_details?.[0];
  const shippingAddress = data?.order_shipping_address_details;
  const billing = data?.order_billing_address_details;
  const shipment = data?.shipment;
  const manual = data?.manual_shipment;

  const fulfilmentCost = (
    parseFloat(data?.fulfillment_cost || 0) +
    parseFloat(AdditionalCostTotal || 0)
  ).toFixed(2);

  const parcelCount =
    shipment?.shipment_details?.length || shipment?.parcel_id?.length || 0;

  const carrier =
    shipment?.shipping_company?.shipping_company?.name ||
    manual?.manual_shipping_company ||
    manual?.shipping_company?.shipping_company?.name;

  const pickupWindow = [shipment?.readyopentime, shipment?.readyclosetime]
    .filter(Boolean)
    .join("–");

  const latestHistory = shipment?.shipment_histories?.[
    shipment.shipment_histories.length - 1
  ];
  const shipmentStatus = shipment?.shipment_status || manual?.shipment_status;
  const deliveryBadge = shipmentStatus
    ? [
        shipmentStatus,
        latestHistory?.StatusDate
          ? `${latestHistory.StatusDate.slice(0, 10)} ${latestHistory.StatusDate.slice(11, 16)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="flex flex-col gap-2">
      {/* Phones: one card per row. From sm up: two, then four from xl. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1.15fr_0.85fr]">
        <Card label={t("orderView.shipper")}>
          <div className="break-words text-[13.5px] font-semibold text-ui-text sm:truncate">
            {pickup?.attention ||
              pickup?.company ||
              data?.user?.business_name ||
              "—"}
          </div>
          <div className="mt-1 line-clamp-2 text-[12px] leading-[1.5] text-ui-text-muted lg:text-[11.5px]">
            {[
              [pickup?.full_address, pickup?.zip_postal]
                .filter(Boolean)
                .join(" ") || data?.user?.address,
              pickup?.phone,
              pickup?.email,
            ]
              .filter(Boolean)
              .join(" · ") || "—"}
          </div>
        </Card>

        <Card label={t("orderView.recipient")}>
          <div className="break-words text-[13.5px] font-semibold text-ui-text sm:truncate">
            {shippingAddress?.first_name || billing?.company || "—"}
          </div>
          <div className="mt-1 line-clamp-2 text-[12px] leading-[1.5] text-ui-text-muted lg:text-[11.5px]">
            {[composeAddress(shippingAddress), billing?.phone, billing?.email]
              .filter(Boolean)
              .join(" · ") || "—"}
          </div>
        </Card>

        <Card label={t("orderView.order")} className="sm:col-span-2 xl:col-span-1">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3">
            <Field label={t("orderView.ordered")} value={convertTime(data?.created_at)} />
            <Field
              label={t("orderView.createdBy")}
              value={
                data?.created_by?.username?.trim() ||
                data?.user?.username?.trim()
              }
            />
            <Field label={t("orderView.type")} value={data?.type} />
            <Field label={t("orderView.payment")} value={data?.payment_method_title} />
            {showPending && (
              <Field
                label={t("orderView.pending")}
                value={data?.amount_pending ? `${data.amount_pending} $` : "0 $"}
              />
            )}
            <Field label={t("orderView.status")} value={data?.status} accent />
          </div>
        </Card>

        {showPrices && (
        <div className="min-w-0 rounded-lg bg-ui-inverted px-3 py-2.5 sm:col-span-2 xl:col-span-1">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/50">
            {t("orderView.fulfilmentCost")}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-semibold leading-none text-white">
              {fulfilmentCost}
            </span>
            <span className="text-[11px] text-white/60">{t("units.cad")}</span>
          </div>
          <div className="mt-2 truncate text-[12px] text-white/60 lg:text-[11px]">
            {[
              shipment?.total_weight
                ? t("orderView.billableWeight", {
                    weight: shipment.total_weight,
                    unit: weightUnit(shipment?.unit),
                  })
                : null,
              parcelCount
                ? t("orderView.parcelCount", { count: parcelCount })
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || t("orderView.noShipmentYet")}
          </div>
        </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-ui-border bg-ui-surface px-3 py-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-1.5">
        <StripItem label={t("orderView.carrier")} value={carrier} />
        <StripItem label={t("orderView.service")} value={shipment?.service_type} />
        {/* As View Shipment shows them: the shipment's type and the
            carrier's pickup reference. */}
        <StripItem label={t("orderView.type")} value={shipment?.shipment_type} />
        <StripItem
          label={t("orderView.pickup")}
          value={
            shipment?.PickupDate?.slice(0, 10) ||
            manual?.pickup_date?.slice(0, 10)
          }
        />
        <StripItem label={t("orderView.window")} value={pickupWindow} />
        <StripItem
          label="Pickup ref"
          value={shipment?.PickupID || manual?.pickup_id}
        />
        <StripItem label="Bolt" value={shipment?.tracking_id} link />
        <StripItem
          label={t("orderView.carrierId")}
          value={shipment?.shipment_tracking_id || manual?.shipment_tracking_id}
          link
        />

        {deliveryBadge && (
          <span className="col-span-2 justify-self-end rounded-md bg-state-success-bg px-2 py-1 text-[11px] font-medium text-state-success-text sm:ml-auto sm:col-span-1 sm:shrink-0">
            {deliveryBadge}
          </span>
        )}
      </div>
    </div>
  );
}
